import { Job } from '@/types/job';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { MapPin, Calendar, Building, Mail, Send, ArrowLeft, Euro, Clock, Users, Award, Timer, Calendar as CalendarIcon, User, Phone, Share } from 'lucide-react';
import { JobDescriptionContent } from './JobDescriptionContent';
import { extractJobInfo } from '@/utils/jobInfoExtractor';
import portraitImage from '@/assets/portrait-authentic.jpg';
import { toast } from 'sonner';

interface JobDetailProps {
  job: Job;
  onBack: () => void;
}

export const JobDetail = ({
  job,
  onBack
}: JobDetailProps) => {
  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    try {
      return new Date(dateString).toLocaleDateString('de-DE');
    } catch {
      return dateString;
    }
  };

  const generateApplicationUrl = (job: Job) => {
    // Use the correct application URL format
    return `https://k61286.coveto.de/public/bewerbung/?id=${job.id}`;
  };

  const handleApply = () => {
    const applicationUrl = generateApplicationUrl(job);
    window.open(applicationUrl, '_blank');
  };

  // Extract image from description if available
  const extractImageFromDescription = (description: string) => {
    const imgMatch = description.match(/background-image:\s*url\("([^"]+)"\)|<img[^>]+src="([^"]+)"/i);
    return imgMatch ? imgMatch[1] || imgMatch[2] : null;
  };

  const imageUrl = job.imageUrl || job.companyLogo || extractImageFromDescription(job.description) || 'https://k61286.coveto.de/public_data/jobs/fotos/Restaurantleitung-hippes-Konzept.png';
  const jobInfo = extractJobInfo(job.description);

  return (
    <div className="w-full">
      {/* Back Button */}
      <div className="container mx-auto px-4 mb-6">
        <Button variant="outline" onClick={onBack} className="flex items-center gap-2 bg-card/90 backdrop-blur-sm border-border/40 hover:bg-card shadow-sm text-sm sm:text-base px-3 sm:px-4 py-2 h-auto min-h-[40px]">
          <ArrowLeft className="w-4 h-4" />
          <span className="hidden sm:inline">Zurück zur Übersicht</span>
          <span className="sm:hidden">Zurück</span>
        </Button>
      </div>

      {/* Hero Section - Full Width */}
      <div className="relative h-72 sm:h-96 md:h-[500px] lg:h-[600px] overflow-hidden mb-8">
        <div className="absolute inset-0 bg-cover bg-[center_25%] sm:bg-[center_30%] md:bg-top bg-no-repeat" style={{
          backgroundImage: `url(${imageUrl})`
        }}>
          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-black/30"></div>
          <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-transparent to-secondary/20"></div>
        </div>
        
        {/* Floating badges */}
        {(jobInfo.workingTime || job.employmentType) && (
          <div className="absolute top-3 sm:top-6 right-3 sm:right-6">
            <div className="glass-effect backdrop-blur-md bg-primary/20 border border-primary/30 rounded-full px-3 sm:px-4 py-1.5 sm:py-2">
              <span className="text-white text-xs sm:text-sm font-medium">
                {jobInfo.workingTime || job.employmentType}
              </span>
            </div>
          </div>
        )}
        
        {/* Content overlay */}
        <div className="absolute inset-0 flex flex-col justify-end">
          <div className="container mx-auto px-4 sm:px-6 md:px-8 py-8">
            <div className="space-y-3 sm:space-y-6 animate-fade-in text-white">
              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-display font-bold leading-tight drop-shadow-lg">
                {job.title}
              </h1>
              
              <div className="flex flex-wrap items-center gap-2 sm:gap-4 md:gap-6 text-sm sm:text-base">
                {job.location && (
                  <div className="flex items-center gap-2 sm:gap-3 glass-effect backdrop-blur-sm bg-white/10 rounded-full px-3 sm:px-4 py-1.5 sm:py-2">
                    <MapPin className="w-4 h-4 sm:w-5 sm:h-5" />
                    <span className="font-medium">{job.location}</span>
                  </div>
                )}
                
                {job.publishDate && (
                  <div className="flex items-center gap-2 sm:gap-3 glass-effect backdrop-blur-sm bg-white/10 rounded-full px-3 sm:px-4 py-1.5 sm:py-2">
                    <Calendar className="w-4 h-4 sm:w-5 sm:h-5" />
                    <span>{formatDate(job.publishDate)}</span>
                  </div>
                )}
              </div>
              
              {/* Key info badges */}
              <div className="flex flex-wrap gap-2 sm:gap-3 pt-1 sm:pt-2">
                {jobInfo.hours && (
                  <Badge className="glass-effect backdrop-blur-md bg-white/15 text-white border border-white/20 px-2 sm:px-3 py-1 sm:py-2 text-xs sm:text-sm">
                    <Timer className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                    {jobInfo.hours}
                  </Badge>
                )}
                {jobInfo.contractType && (
                  <Badge className="glass-effect backdrop-blur-md bg-white/15 text-white border border-white/20 px-2 sm:px-3 py-1 sm:py-2 text-xs sm:text-sm">
                    <Award className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                    {jobInfo.contractType}
                  </Badge>
                )}
                {jobInfo.startDate && (
                  <Badge className="glass-effect backdrop-blur-md bg-white/15 text-white border border-white/20 px-2 sm:px-3 py-1 sm:py-2 text-xs sm:text-sm">
                    <CalendarIcon className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                    {jobInfo.startDate}
                  </Badge>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content - Two Column Layout */}
      <div className="container mx-auto px-4 space-y-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content - Left Column */}
          <div className="lg:col-span-2 space-y-8">
            {/* Additional Information */}
            {(job.department || job.salary) && (
              <div className="flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4">
                {job.department && (
                  <div className="glass-effect backdrop-blur-sm bg-primary/5 border border-primary/10 rounded-lg sm:rounded-xl px-4 sm:px-6 py-2.5 sm:py-3">
                    <div className="flex items-center gap-2 sm:gap-3">
                      <Building className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
                      <span className="font-medium text-foreground text-sm sm:text-base">{job.department}</span>
                    </div>
                  </div>
                )}
                
                {job.salary && (
                  <div className="glass-effect backdrop-blur-sm bg-secondary/5 border border-secondary/10 rounded-lg sm:rounded-xl px-4 sm:px-6 py-2.5 sm:py-3">
                    <div className="flex items-center gap-2 sm:gap-3">
                      <Euro className="w-4 h-4 sm:w-5 sm:h-5 text-secondary" />
                      <span className="font-medium text-foreground text-sm sm:text-base">{job.salary}</span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Job Description */}
            {job.description && (
              <Card className="bg-gradient-to-r from-primary/5 to-transparent border-0 shadow-lg">
                <CardContent className="p-6 sm:p-8">
                  <JobDescriptionContent description={job.description} />
                </CardContent>
              </Card>
            )}
            
            {/* Requirements */}
            {job.requirements && (
              <Card className="bg-gradient-to-r from-secondary/5 to-transparent border-0 shadow-lg">
                <CardContent className="p-6 sm:p-8">
                  <h2 className="text-xl sm:text-2xl font-display font-semibold mb-4 text-foreground">
                    Anforderungen
                  </h2>
                  <div className="prose prose-sm sm:prose-lg max-w-none text-muted-foreground leading-relaxed">
                    <JobDescriptionContent description={job.requirements} />
                  </div>
                </CardContent>
              </Card>
            )}
            
            {/* Benefits */}
            {job.benefits && (
              <Card className="bg-gradient-to-r from-accent/5 to-transparent border-0 shadow-lg">
                <CardContent className="p-6 sm:p-8">
                  <h2 className="text-xl sm:text-2xl font-display font-semibold mb-4 text-foreground">
                    Das bieten wir
                  </h2>
                  <div className="prose prose-sm sm:prose-lg max-w-none text-muted-foreground leading-relaxed">
                    <JobDescriptionContent description={job.benefits} />
                  </div>
                </CardContent>
              </Card>
            )}
            
            {/* Application Deadline */}
            {job.applicationDeadline && (
              <Card className="bg-muted/30 border-muted border">
                <CardContent className="p-4 sm:p-6">
                  <div className="flex items-center gap-2 sm:gap-3 text-foreground">
                    <Calendar className="w-5 h-5 sm:w-6 sm:h-6 text-primary flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-sm sm:text-base">Bewerbungsschluss</p>
                      <p className="text-muted-foreground text-sm sm:text-base">{formatDate(job.applicationDeadline)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar - Right Column */}
          <div className="lg:col-span-1 lg:sticky lg:top-24 lg:self-start space-y-6">
            {/* Sticky Contact & Application Card */}
            <div>
              <Card className="bg-primary/5 border-primary/10 border shadow-elegant">
                {/* Header */}
                <div className="bg-gradient-to-r from-primary/10 to-secondary/5 p-4 border-b border-border/10">
                  <h3 className="text-lg font-semibold text-foreground text-center">
                    Bereit für den nächsten Schritt?
                  </h3>
                  <p className="text-sm text-muted-foreground text-center mt-1">
                    Kontaktieren Sie uns direkt
                  </p>
                </div>
                
                <CardContent className="p-4 space-y-4">
                  {/* Contact Person */}
                  <div className="text-center">
                    <div className="w-20 h-20 mx-auto rounded-full overflow-hidden border-2 border-primary/20 shadow-lg bg-gradient-to-br from-primary/5 to-secondary/5 mb-3">
                      <img 
                        src={job.contactImage || "https://k61286.coveto.de/public_data/amaBenutzer/3/3.jpg" || portraitImage} 
                        alt={job.contactPerson || "Michael Scholz"} 
                        className="w-full h-full object-cover" 
                        onError={(e) => {
                          e.currentTarget.src = portraitImage;
                        }} 
                      />
                    </div>
                    <h4 className="font-semibold text-foreground">
                      {job.contactPerson || "Michael Scholz"}
                    </h4>
                    <p className="text-sm text-primary font-medium">
                      {job.contactTitle || "Geschäftsführer"}
                    </p>
                  </div>

                  {/* Contact Information */}
                  <div className="space-y-3">
                    <div className="bg-white/50 rounded-lg p-3 border border-border/20">
                      <div className="text-center space-y-2">
                        <div className="flex items-center justify-center gap-2">
                          <Phone className="w-4 h-4 text-primary" />
                          <span className="text-sm font-medium text-muted-foreground">Telefon</span>
                        </div>
                        <a href={`tel:${job.contactPhone || "+494141-4263117"}`} 
                           className="block font-semibold text-foreground hover:text-primary transition-colors">
                          {job.contactPhone || "+49 4141 - 4263117"}
                        </a>
                      </div>
                    </div>
                    
                    <div className="bg-white/50 rounded-lg p-3 border border-border/20">
                      <div className="text-center space-y-2">
                        <div className="flex items-center justify-center gap-2">
                          <Mail className="w-4 h-4 text-primary" />
                          <span className="text-sm font-medium text-muted-foreground">E-Mail</span>
                        </div>
                        <a href={`mailto:${job.contactEmail || "m.scholz@scholz-interim.de"}?subject=Bewerbung: ${job.title}`} 
                           className="block font-semibold text-foreground hover:text-primary transition-colors text-sm leading-tight">
                           {job.contactEmail || "m.scholz@scholz-interim.de"}
                        </a>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="space-y-2 pt-2">
                    <Button onClick={handleApply} size="default" className="w-full bg-primary hover:bg-primary/90 text-white font-semibold">
                      <Send className="w-4 h-4 mr-2" />
                      Zur Online-Bewerbung
                    </Button>
                    
                    {job.contactEmail && (
                      <Button variant="outline" size="default" 
                              onClick={() => window.location.href = `mailto:${job.contactEmail}?subject=Bewerbung: ${job.title}`} 
                              className="w-full border-primary/20 hover:bg-primary/5 hover:text-foreground">
                        <Mail className="w-4 h-4 mr-2" />
                        E-Mail senden
                      </Button>
                    )}
                  </div>

                  {/* Company Address */}
                  <div className="bg-muted/30 rounded-lg p-3 border border-border/20 text-center">
                    <div className="text-muted-foreground space-y-1 text-sm">
                      <p className="font-medium text-foreground">Scholz & Scholz Recruitment U.G</p>
                      <p>{job.contactAddress || "Römerstraße 6"}</p>
                      <p>{job.contactPostalCode || "21680"} {job.contactCity || "Stade"}</p>
                    </div>
                  </div>
                  
                  {/* Share Section */}
                  <div className="border-t border-border/10 pt-3">
                    <div className="text-center mb-2">
                      <p className="text-sm font-medium text-foreground">
                        Perfekt für einen Bekannten?
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Teilen Sie diese Stellenanzeige
                      </p>
                    </div>
                    <Button variant="outline" size="sm" 
                            className="border-primary/30 hover:bg-primary/5 hover:border-primary/50 hover:text-foreground w-full"
                            onClick={async () => {
                              const jobUrl = `${window.location.origin}/stellenangebote/${job.id}`;
                              const shareText = `${job.title} bei ${job.company} in ${job.location}`;
                              
                              console.log('Share button clicked', { jobUrl, shareText });
                              
                              try {
                                if (navigator.share && window.isSecureContext) {
                                  console.log('Using native share');
                                  await navigator.share({
                                    title: `Stellenanzeige: ${job.title}`,
                                    text: shareText,
                                    url: jobUrl
                                  });
                                  console.log('Native share successful');
                                } else {
                                  console.log('Using clipboard fallback');
                                  await navigator.clipboard.writeText(`${shareText} - ${jobUrl}`);
                                  console.log('Clipboard write successful');
                                  toast.success("Link in die Zwischenablage kopiert!");
                                }
                              } catch (error) {
                                console.error('Share error:', error);
                                // Fallback: Einfach den Text in ein temporäres Element kopieren
                                try {
                                  const textArea = document.createElement('textarea');
                                  textArea.value = `${shareText} - ${jobUrl}`;
                                  document.body.appendChild(textArea);
                                  textArea.select();
                                  document.execCommand('copy');
                                  document.body.removeChild(textArea);
                                  toast.success("Link in die Zwischenablage kopiert!");
                                } catch (fallbackError) {
                                  console.error('Fallback error:', fallbackError);
                                  toast.error("Fehler beim Teilen. Versuchen Sie es erneut.");
                                }
                              }
                             }}>
                      <Share className="w-4 h-4 mr-2" />
                      Teilen
                    </Button>
                  </div>

                  {/* Back Button */}
                  <div className="border-t border-border/10 pt-3">
                    <Button variant="outline" onClick={onBack} 
                            className="w-full flex items-center justify-center gap-2 bg-card/50 border-border/40 hover:bg-card hover:text-foreground text-sm">
                      <ArrowLeft className="w-4 h-4" />
                      Zurück zur Übersicht
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};